import { useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";

interface Invitation {
  tenantId: string;
  tenantName: string;
  tenantRole: string;
  createdAt: string;
}

/**
 * App-wide banner (signed-in users): pending workspace invitations to accept or decline. A pending
 * invite grants no access until accepted — you keep working in your own workspace and this sits at the
 * top until you decide. Accepting makes the workspace switchable (full reload so the switcher refreshes);
 * declining removes the invitation. Self-hides when there's nothing pending.
 */
export function PendingInvites() {
  const { data, reload } = useAsync<{ invitations: Invitation[] }>(() => api("/me/invitations"), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const invites = data?.invitations ?? [];
  if (invites.length === 0) return null;

  async function act(tenantId: string, kind: "accept" | "decline") {
    setBusy(tenantId);
    setErr(null);
    try {
      if (kind === "accept") {
        await api(`/me/invitations/${tenantId}/accept`, { method: "POST" });
        // The workspace is now active — reload so the org switcher + memberships pick it up.
        window.location.reload();
        return;
      }
      await api(`/me/invitations/${tenantId}`, { method: "DELETE" });
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="invite-banners">
      {invites.map((iv) => (
        <div key={iv.tenantId} className="invite-banner" role="status">
          <span className="invite-banner-text">
            You've been invited to <strong>{iv.tenantName}</strong> as {iv.tenantRole}.
          </span>
          <div className="row-gap" style={{ gap: "var(--sp2)" }}>
            <button className="btn sm" disabled={busy === iv.tenantId} onClick={() => void act(iv.tenantId, "accept")}>
              {busy === iv.tenantId ? "…" : "Accept"}
            </button>
            <button className="btn ghost sm" disabled={busy === iv.tenantId} onClick={() => void act(iv.tenantId, "decline")}>
              Decline
            </button>
          </div>
        </div>
      ))}
      {err && <div className="err">{err}</div>}
    </div>
  );
}
